import { Chromosome } from '../types/algorithm';
import { calculateChromosomeFitness } from '../utils/fitnessCalculator';
import {
  initializePopulation,
  tournamentSelection,
  singlePointCrossover,
  mutate,
  findBestChromosome,
} from './geneticAlgorithm';
import { SeededRNG, createSeededRNG } from '../utils/seededRNG';

/**
 * Cultural Algorithm implementation from scratch
 * Extends GA with belief space (elite-based selection for crossover)
 */

/**
 * Selects a parent with elite bias (belief-space influence)
 * Uses selectionRate to determine if using tournament selection from whole population
 * or biasing toward elites
 */
function selectParentWithEliteBias(
  population: Chromosome[],
  elite: Chromosome[],
  eliteIndices: number[],
  selectionRate: number,
  useEliteBias: boolean,
  rng: SeededRNG
): number {
  // Use tournament selection from whole population if selectionRate probability
  if (rng.randomChance(selectionRate / 100)) {
    return tournamentSelection(population, rng);
  }
  
  // Otherwise, bias toward elites if useEliteBias is true
  if (useEliteBias && rng.randomChance(0.7)) {
    // 70% chance to select from elite
    const eliteIdx = rng.randomInt(elite.length);
    return eliteIndices[eliteIdx];
  }
  
  // Random selection from whole population using seeded RNG
  return rng.randomInt(population.length);
}

/**
 * Main Cultural Algorithm evolution step
 */
export function evolveCultural(
  population: Chromosome[],
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  selectionRate: number,
  crossoverRate: number,
  mutationRate: number,
  eliteCount: number,
  rng: SeededRNG
): void {
  const popSize = population.length;
  
  // Sort population by fitness (descending) to identify elites
  const sortedWithIndices = population
    .map((chrom, idx) => ({ chrom, idx }))
    .sort((a, b) => b.chrom.fitness - a.chrom.fitness);
  
  const elite = sortedWithIndices.slice(0, eliteCount).map(item => item.chrom);
  const eliteIndices = sortedWithIndices.slice(0, eliteCount).map(item => item.idx);
  
  // Compute explicit even number of pairs for crossover
  const numPairs = Math.floor(popSize / 2);
  const mutateCount = Math.floor((popSize * mutationRate) / 100);

  // Build mating pool and perform crossover with elite bias
  for (let i = 0; i < numPairs; i++) {
    // Apply crossover with crossoverRate probability per mating event
    if (!rng.randomChance(crossoverRate / 100)) {
      continue; // Skip this pair if crossover doesn't occur
    }
    
    // Select parents with elite bias (belief-space influence)
    // First parent: occasionally force elite (30% chance), otherwise use selectionRate
    const forceEliteParent1 = rng.randomChance(0.3);
    const parent1Idx = forceEliteParent1
      ? eliteIndices[rng.randomInt(elite.length)]
      : selectParentWithEliteBias(population, elite, eliteIndices, selectionRate, true, rng);
    
    // Second parent: use selectionRate with elite bias
    const parent2Idx = selectParentWithEliteBias(population, elite, eliteIndices, selectionRate, true, rng);
    
    // Ensure different parents (with retry to avoid adjacent selection)
    let finalParent2Idx = parent2Idx;
    if (finalParent2Idx === parent1Idx && popSize > 1) {
      // Try to re-select parent2 a few times
      let retries = 0;
      const maxRetries = 5;
      while (finalParent2Idx === parent1Idx && retries < maxRetries) {
        finalParent2Idx = selectParentWithEliteBias(population, elite, eliteIndices, selectionRate, true, rng);
        retries++;
      }
      // If still same after retries, use next index (but not adjacent if possible)
      if (finalParent2Idx === parent1Idx) {
        finalParent2Idx = (parent1Idx + Math.floor(popSize / 2)) % popSize;
      }
    }

    const [child1, child2] = singlePointCrossover(
      population[parent1Idx],
      population[finalParent2Idx],
      taskCount,
      rng
    );

    // Evaluate offspring
    calculateChromosomeFitness(child1, matrix, taskCount, serviceCount);
    calculateChromosomeFitness(child2, matrix, taskCount, serviceCount);

    // Replace worst individuals if offspring are better
    // Fixed: Re-sort population to find current worst individuals after crossovers
    const currentSortedWithIndices = population
      .map((chrom, idx) => ({ chrom, idx }))
      .sort((a, b) => a.chrom.fitness - b.chrom.fitness); // Sort ascending (worst first)

    const worst1Idx = currentSortedWithIndices[0]?.idx;
    const worst2Idx = currentSortedWithIndices.length > 1 
      ? currentSortedWithIndices[1]?.idx 
      : worst1Idx;

    if (worst1Idx !== undefined && child1.fitness > population[worst1Idx].fitness) {
      population[worst1Idx] = child1;
    }
    if (worst2Idx !== undefined && worst2Idx !== worst1Idx && child2.fitness > population[worst2Idx].fitness) {
      population[worst2Idx] = child2;
    }
  }

  // Mutation operations
  for (let i = 0; i < mutateCount; i++) {
    const idx = rng.randomInt(popSize);
    const mutated = mutate(population[idx], taskCount, serviceCount, rng);
    calculateChromosomeFitness(mutated, matrix, taskCount, serviceCount);

    if (mutated.fitness > population[idx].fitness) {
      population[idx] = mutated;
    }
  }
}

/**
 * Runs Cultural Algorithm for specified generations
 */
export async function runCulturalAlgorithm(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  parameters: {
    populationSize: number;
    generations: number;
    selectionRate: number;
    crossoverRate: number;
    mutationRate: number;
  },
  onGenerationComplete?: (generation: number, bestFitness: number) => void,
  seed?: number
): Promise<Chromosome> {
  // Create seeded RNG for reproducibility
  const rng = createSeededRNG(seed);
  
  // Initialize population
  let population = initializePopulation(
    parameters.populationSize,
    taskCount,
    serviceCount,
    rng
  );

  // Evaluate initial population
  for (const chrom of population) {
    calculateChromosomeFitness(chrom, matrix, taskCount, serviceCount);
  }

  // Elite count (typically 10% of population)
  const eliteCount = Math.max(1, Math.floor(parameters.populationSize * 0.1));

  // Evolution loop
  let lastUpdateTime = Date.now();
  const UPDATE_INTERVAL = 100; // Update UI every 100ms (PERFORMANCE OPTIMIZATION)
  
  for (let gen = 0; gen < parameters.generations; gen++) {
    evolveCultural(
      population,
      taskCount,
      serviceCount,
      matrix,
      parameters.selectionRate,
      parameters.crossoverRate,
      parameters.mutationRate,
      eliteCount,
      rng
    );

    // Batch UI updates - only update every 100ms or every 10 generations (PERFORMANCE OPTIMIZATION)
    const now = Date.now();
    const shouldUpdate = (now - lastUpdateTime > UPDATE_INTERVAL) || (gen % 10 === 9) || (gen === parameters.generations - 1);
    
    if (shouldUpdate) {
      const best = findBestChromosome(population);
      if (onGenerationComplete) {
        onGenerationComplete(gen + 1, best.fitness);
      }
      lastUpdateTime = now;
      // Yield control to allow UI updates
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  }

  return findBestChromosome(population);
}

